/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import BookingAction, {
    PackageCompetition,
    PackageUpsell,
    PaymentAssert,
    ReservationNote
} from "store/booking/BookingAction";
import {Activity} from "types/entities/Activity";
import update from "immutability-helper";
import {Package} from "types/entities/Package";
import {BookingFilter, BookingProcess, BookingTimeslot} from "types/app";
import {Company} from "types/entities/Company";
import {PackagesCart} from "types/entities/PackagesCart";
import {Cookies} from "types/entities/Cookies";
import {Cookies_status} from "types/enums/Cookies_status";
import {Payment} from "types/entities/Payment";
import {Timeslot} from "types/entities/Timeslot";
import {PackageCategory} from "types/enums/PackageCategory";
import {CreditItem, PackagesItemByPerson, PackagesItemsResponse} from "types/entities/PackagesItems";
import {RouteName} from "utils/hooks/useNavigation";
import {PackageInfo, PackageItemCompetition} from "types/entities/PackageItem";

export interface BookingReducerSetActivitiesAction {
    payload: Activity[];
    type: typeof BookingAction.SET_ACTIVITIES;
}

export interface BookingReducerSetPackagesAction {
    payload: Package[];
    type: typeof BookingAction.SET_PACKAGES;
}

export interface BookingReducerSetAvailablePackagesItemsAction {
    payload: PackagesItemsResponse;
    type: typeof BookingAction.SET_AVAILABLE_PACKAGES_ITEMS;
}

export interface BookingReducerClearAvailablePackagesItemsAction {
    payload: null;
    type: typeof BookingAction.CLEAR_AVAILABLE_PACKAGES_ITEMS;
}

export interface BookingReducerSetAvailableCreditItemsAction {
    payload: CreditItem[];
    type: typeof BookingAction.SET_AVAILABLE_CREDIT_ITEMS;
}

export interface BookingReducerClearAvailableCreditItemsAction {
    payload: [];
    type: typeof BookingAction.CLEAR_AVAILABLE_CREDIT_ITEMS;
}

export interface BookingReducerSetSelectedPackageItemsAction {
    payload: PackagesItemByPerson[];
    type: typeof BookingAction.SET_SELECTED_PACKAGE_ITEMS;
}

export interface BookingReducerSetSelectedPackageItemAction {
    payload: PackagesItemByPerson;
    type: typeof BookingAction.SET_SELECTED_PACKAGE_ITEM;
}

export interface BookingReducerResetSelectedPackageItemsAction {
    payload: [];
    type: typeof BookingAction.RESET_SELECTED_PACKAGE_ITEMS;
}

export interface BookingReducerSetSelectedPackageCreditItemsAction {
    payload: PackagesItemByPerson[];
    type: typeof BookingAction.SET_SELECTED_PACKAGE_CREDIT_ITEMS;
}

export interface BookingReducerSetSelectedPackageCreditItemAction {
    payload: PackagesItemByPerson;
    type: typeof BookingAction.SET_SELECTED_PACKAGE_CREDIT_ITEM;
}

export interface BookingReducerRemoveSelectedPackageCreditItemAction {
    payload: number;
    type: typeof BookingAction.REMOVE_SELECTED_PACKAGE_CREDIT_ITEM;
}

export interface BookingReducerResetSelectedPackageCreditItemsAction {
    payload: [];
    type: typeof BookingAction.RESET_SELECTED_PACKAGE_CREDIT_ITEMS;
}

export interface BookingReducerSetTimeslotsAction {
    payload: BookingTimeslot[];
    type: typeof BookingAction.SET_TIMESLOTS;
}

export interface BookingReducerSetTimeslotsAllowedAction {
    payload: boolean;
    type: typeof BookingAction.SET_TIMESLOTS_ALLOWED;
}

export interface BookingReducerSetPackageCartAction {
    payload: PackagesCart;
    type: typeof BookingAction.SET_PACKAGE_CART;
}

export interface BookingReducerSetDateChosenByCustomerAction {
    payload: boolean;
    type: typeof BookingAction.SET_DATE_CHOSEN_BY_CUSTOMER;
}

export interface BookingReducerSetEmailVerifiedAction {
    payload: boolean;
    type: typeof BookingAction.SET_EMAIL_VERIFIED;
}

export interface BookingReducerSetLoginVerifiedAction {
    payload: boolean;
    type: typeof BookingAction.SET_LOGIN_VERIFIED;
}

export interface BookingReducerSetFilterAction {
    payload: BookingFilter;
    type: typeof BookingAction.SET_FILTER;
}

export interface BookingReducerSetFilterDrivesAdultCountAction {
    payload: number;
    type: typeof BookingAction.SET_FILTER_ADULTS_COUNT;
}

export interface BookingReducerSetFilterDrivesChildCountAction {
    payload: number;
    type: typeof BookingAction.SET_FILTER_CHILDREN_COUNT;
}

export interface BookingReducerSetPackageAction {
    payload: Package | null;
    type: typeof BookingAction.SET_PACKAGE;
}

export interface BookingReducerPushReservationNoteAction {
    payload: ReservationNote;
    type: typeof BookingAction.PUSH_RESERVATION_NOTE;
}

export interface BookingReducerResetReservationNoteAction {
    payload: null | ReservationNote;
    type: typeof BookingAction.RESET_RESERVATION_NOTE;
}

export interface BookingReducerPushUpsellAction {
    payload: PackageUpsell;
    type: typeof BookingAction.PUSH_UPSELL;
}

export interface BookingReducerSetUpsellAction {
    payload: PackageUpsell;
    type: typeof BookingAction.SET_UPSELL;
}

export interface BookingReducerRemoveUpsellAction {
    payload: PackageUpsell;
    type: typeof BookingAction.REMOVE_UPSELL;
}

export interface BookingReducerPushCompetitionAction {
    payload: PackageCompetition;
    type: typeof BookingAction.PUSH_COMPETITION;
}

export interface BookingReducerSetPackageInfoAction {
    payload: PackageInfo;
    type: typeof BookingAction.SET_PACKAGE_INFO;
}

export interface BookingReducerSetRenounceFreeCompetitionsAction {
    payload: boolean;
    type: typeof BookingAction.SET_RENOUNCE_FREE_COMPETITIONS;
}

export interface BookingReducerSetCompetitionAction {
    payload: PackageCompetition;
    type: typeof BookingAction.SET_COMPETITION;
}

export interface BookingReducerRemoveCompetitionAction {
    payload: PackageCompetition;
    type: typeof BookingAction.REMOVE_COMPETITION;
}

export interface BookingReducerClearCompetitionsAction {
    payload: any[];
    type: typeof BookingAction.CLEAR_COMPETITIONS;
}

export interface BookingReducerSetCustomerAction {
    payload: Company;
    type: typeof BookingAction.SET_CUSTOMER;
}

export interface BookingReducerResetCustomerAction {
    payload: null;
    type: typeof BookingAction.RESET_CUSTOMER;
}

export interface BookingReducerSetCookiesAction {
    payload: Cookies;
    type: typeof BookingAction.SET_COOKIES;
}

export interface BookingReducerSetPackagesCartPaymentAction {
    payload: Payment | null;
    type: typeof BookingAction.SET_PACKAGES_CART_PAYMENT;
}

export interface BookingReducerSetPackagesCartPaymentAssertAction {
    payload: PaymentAssert | null;
    type: typeof BookingAction.SET_PACKAGES_CART_PAYMENT_ASSERT;
}

export interface BookingReducerSetCurrentNavigationStepAction {
    payload: BookingNavigationStep;
    type: typeof BookingAction.SET_CURRENT_NAVIGATION_STEP;
}

export interface BookingReducerClearBookingProcessAction {
    payload: null;
    type: typeof BookingAction.CLEAR_BOOKING_PROCESS_STATE;
}

export interface BookingReducerClearUpsellsAction {
    payload: any[];
    type: typeof BookingAction.CLEAR_UPSELLS;
}

export interface BookingReducerSetPossibleTimeslotsAction {
    payload: Timeslot[];
    type: typeof BookingAction.SET_POSSIBLE_TIMESLOTS;
}

export interface BookingReducerSetCustomTimeslotsAction {
    payload: Timeslot;
    type: typeof BookingAction.SET_CUSTOM_TIMESLOT;
}

export interface BookingReducerSetSelectedTimeslotAction {
    payload: Timeslot;
    type: typeof BookingAction.SET_SELECTED_TIMESLOT;
}

export interface BookingReducerResetCustomTimeslotsAction {
    payload: [];
    type: typeof BookingAction.RESET_CUSTOM_TIMESLOTS;
}

export interface BookingReducerSetAvailableCompetitionItemsAction {
    payload: PackageItemCompetition[];
    type: typeof BookingAction.SET_AVAILABLE_COMPETITION_ITEMS;
}

export interface BookingReducerClearAvailableCompetitionItemsAction {
    payload: [];
    type: typeof BookingAction.CLEAR_AVAILABLE_COMPETITION_ITEMS;
}

export interface BookingReducerSetAvailableIncludedCompetitionItemsAction {
    payload: PackageItemCompetition[];
    type: typeof BookingAction.SET_AVAILABLE_INCLUDED_COMPETITION_ITEMS;
}

export interface BookingReducerClearAvailableIncludedCompetitionItemsAction {
    payload: [];
    type: typeof BookingAction.CLEAR_AVAILABLE_INCLUDED_COMPETITION_ITEMS;
}

type BookingReducerAction =
    | BookingReducerSetActivitiesAction
    | BookingReducerSetPackagesAction
    | BookingReducerSetTimeslotsAction
    | BookingReducerSetTimeslotsAllowedAction
    | BookingReducerSetPackageCartAction
    | BookingReducerSetLoginVerifiedAction
    | BookingReducerSetEmailVerifiedAction
    | BookingReducerSetDateChosenByCustomerAction
    | BookingReducerSetFilterAction
    | BookingReducerSetFilterDrivesAdultCountAction
    | BookingReducerSetFilterDrivesChildCountAction
    | BookingReducerSetPackageAction
    | BookingReducerPushReservationNoteAction
    | BookingReducerResetReservationNoteAction
    | BookingReducerSetUpsellAction
    | BookingReducerPushUpsellAction
    | BookingReducerRemoveUpsellAction
    | BookingReducerSetCompetitionAction
    | BookingReducerPushCompetitionAction
    | BookingReducerRemoveCompetitionAction
    | BookingReducerClearCompetitionsAction
    | BookingReducerResetCustomerAction
    | BookingReducerSetCustomerAction
    | BookingReducerSetCookiesAction
    | BookingReducerSetPackagesCartPaymentAction
    | BookingReducerSetPackagesCartPaymentAssertAction
    | BookingReducerSetCurrentNavigationStepAction
    | BookingReducerClearBookingProcessAction
    | BookingReducerSetPossibleTimeslotsAction
    | BookingReducerSetCustomTimeslotsAction
    | BookingReducerResetCustomTimeslotsAction
    | BookingReducerClearUpsellsAction
    | BookingReducerSetSelectedTimeslotAction
    | BookingReducerSetAvailablePackagesItemsAction
    | BookingReducerResetSelectedPackageItemsAction
    | BookingReducerSetSelectedPackageItemAction
    | BookingReducerSetSelectedPackageItemsAction
    | BookingReducerSetSelectedPackageCreditItemsAction
    | BookingReducerSetSelectedPackageCreditItemAction
    | BookingReducerRemoveSelectedPackageCreditItemAction
    | BookingReducerResetSelectedPackageCreditItemsAction
    | BookingReducerClearAvailablePackagesItemsAction
    | BookingReducerSetAvailableCreditItemsAction
    | BookingReducerClearAvailableCreditItemsAction
    | BookingReducerSetAvailableCompetitionItemsAction
    | BookingReducerClearAvailableCompetitionItemsAction
    | BookingReducerSetAvailableIncludedCompetitionItemsAction
    | BookingReducerClearAvailableIncludedCompetitionItemsAction
    | BookingReducerSetRenounceFreeCompetitionsAction
    | BookingReducerSetPackageInfoAction;

export interface BookingNavigation {
    currentStep: BookingNavigationStep;
    timeslotsAllowedSteps: BookingNavigationStep[];
    timeslotsGiftAllowedSteps: BookingNavigationStep[];
    timeslotsNotAllowedSteps: BookingNavigationStep[];
}

export interface BookingNavigationStep {
    id: number;
    label: string;
}

export interface AllowedStep {
    id: number;
    label: string;
    routeName: RouteName;
}

export const TIMESLOTS_ALLOWED_STEPS: AllowedStep[] = [
    {id: 1, label: "Home", routeName: RouteName.Main},
    {id: 2, label: "Options", routeName: RouteName.Options},
    {id: 3, label: "Timeslots", routeName: RouteName.TimeslotsNew},
    {id: 4, label: "Tickets & Credits", routeName: RouteName.Packages},
    {id: 5, label: "Recapitulation", routeName: RouteName.Customer},
    {id: 6, label: "Payment", routeName: RouteName.Paid},
];

export const TIMESLOTS_ALLOWED_STEPS_FOR_GIFT: AllowedStep[] = [
    {id: 1, label: "Home", routeName: RouteName.Main},
    {id: 2, label: "Gift voucher", routeName: RouteName.Gift},
    {id: 3, label: "Checkout", routeName: RouteName.GiftCheckout},
];

export const TIMESLOTS_ALLOWED_STEPS_FOR_SUBSCRIPTION: AllowedStep[] = [
    {id: 1, label: "Home", routeName: RouteName.Main},
    {id: 2, label: "Subscription", routeName: RouteName.ThreeTwo},
    {id: 3, label: "Checkout", routeName: RouteName.GiftCheckout},
];

export const TIMESLOTS_UPSELLS_ALLOWED_STEPS: AllowedStep[] = [
    {id: 1, label: "Home", routeName: RouteName.Main},
    {id: 2, label: "Options", routeName: RouteName.Options},
    {id: 3, label: "Timeslots", routeName: RouteName.TimeslotsNew},
    {id: 4, label: "Tickets & Credits", routeName: RouteName.Packages},
    {id: 5, label: "Competitions", routeName: RouteName.Competitions},
    {id: 6, label: "Upsells", routeName: RouteName.UpsellsNew},
    {id: 7, label: "Recapitulation", routeName: RouteName.Customer},
    {id: 8, label: "Payment", routeName: RouteName.Paid},
];

const TIMESLOT_NOT_ALLOWED_STEPS = [
    {id: 1, label: "Packages"},
    {id: 2, label: "Customer"},
    {id: 3, label: "Paid"},
];

export interface BookingReducerState {
    activities: Activity[];
    packages: Package[];
    timeslotsAllowed: boolean;
    timeslots: BookingTimeslot[];
    bookingProcess: BookingProcess;
    cookies: Cookies;
    navigation: BookingNavigation;
}

const getStandardDate = () => {
    const date = new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based, so we add 1
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};
const initBookingProcess: BookingProcess = {
    filter: {
        adults: 0,
        children: 0,
        date: getStandardDate(),
        activity_ids: [],
        groupType: [],
        hours_count: 0,
        category: PackageCategory.General,
    },
    package: null,
    upsell: [],
    competition: [],
    customer: null,
    packageCart: null,
    voucher: null,
    payment: null,
    paymentAssert: null,
    notes: [],
    customTimeslots: [],
    possibleTimeslots: [],
    dateChosenByCustomer: false,
    emailVerified: false,
    emailLogin: false,
    selectedTimeslot: null,
    availablePackageItems: null,
    availableCreditItems: [],
    selectedPackageItems: [],
    selectedPackageCreditItems: [],
    availableCompetitionItems: [],
    availableIncludedCompetitionItems: [],
    packageInfo: null,
    renounceFreeCompetitions: false,
};

class BookingReducer {
    initialState: BookingReducerState = {
        activities: [],
        packages: [],
        timeslotsAllowed: true,
        timeslots: [],
        bookingProcess: initBookingProcess,
        cookies: {tech_cookies: true, analytics_cookies: false, advert_cookies: false, status: Cookies_status.NULL},
        navigation: {
            currentStep: TIMESLOTS_ALLOWED_STEPS[0],
            timeslotsAllowedSteps: TIMESLOTS_ALLOWED_STEPS,
            timeslotsGiftAllowedSteps: TIMESLOTS_ALLOWED_STEPS_FOR_GIFT,
            timeslotsNotAllowedSteps: TIMESLOT_NOT_ALLOWED_STEPS,
        },
    };

    reducer = (state: BookingReducerState = this.initialState, action: BookingReducerAction): BookingReducerState => {
        switch (action.type) {
            case BookingAction.SET_DATE_CHOSEN_BY_CUSTOMER:
                return update(state, {
                    bookingProcess: {
                        dateChosenByCustomer: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_EMAIL_VERIFIED:
                return update(state, {
                    bookingProcess: {
                        emailVerified: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_LOGIN_VERIFIED:
                return update(state, {
                    bookingProcess: {
                        emailLogin: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_POSSIBLE_TIMESLOTS:
                return update(state, {
                    bookingProcess: {
                        possibleTimeslots: {
                            $set: action.payload,
                        },
                    },
                });
            case BookingAction.SET_CUSTOM_TIMESLOT:
                const timeslotIndex = state.bookingProcess.customTimeslots.findIndex(timeslot => timeslot.id === action.payload.id);
                if (timeslotIndex === -1) {
                    return update(state, {
                        bookingProcess: {
                            customTimeslots: {
                                $push: [action.payload],
                            },
                        },
                    });
                }

                return update(state, {
                    bookingProcess: {
                        customTimeslots: {
                            $splice: [[timeslotIndex, 1]],
                        },
                    },
                });
            case BookingAction.RESET_CUSTOM_TIMESLOTS:
                return update(state, {
                    bookingProcess: {
                        customTimeslots: {
                            $set: [],
                        },
                    },
                });

            case BookingAction.SET_ACTIVITIES:
                return update(state, {
                    activities: {
                        $set: action.payload,
                    },
                });
            case BookingAction.SET_PACKAGES:
                return update(state, {
                    packages: {
                        $set: action.payload,
                    },
                });

            case BookingAction.SET_AVAILABLE_PACKAGES_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availablePackageItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.CLEAR_AVAILABLE_PACKAGES_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availablePackageItems: {
                            $set: null,
                        },
                    },
                });

            case BookingAction.SET_AVAILABLE_CREDIT_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availableCreditItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.CLEAR_AVAILABLE_CREDIT_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availableCreditItems: {
                            $set: [],
                        },
                    },
                });

            case BookingAction.SET_SELECTED_PACKAGE_ITEMS:
                return update(state, {
                    bookingProcess: {
                        selectedPackageItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_SELECTED_TIMESLOT:
                return update(state, {
                    bookingProcess: {
                        selectedTimeslot: {
                            $set: action.payload,
                        },
                    },
                });
            case BookingAction.SET_SELECTED_PACKAGE_ITEM:
                const selectedPackageItemIndex = state.bookingProcess.selectedPackageItems.findIndex(item => item.personIndex === action.payload.personIndex);
                if (selectedPackageItemIndex === -1) {
                    return update(state, {
                        bookingProcess: {
                            selectedPackageItems: {
                                $push: [action.payload],
                            },
                        },
                    });
                }

                return update(state, {
                    bookingProcess: {
                        selectedPackageItems: {
                            [selectedPackageItemIndex]: {
                                $set: action.payload,
                            },
                        },
                    },
                });

            case BookingAction.RESET_SELECTED_PACKAGE_ITEMS:
                return update(state, {
                    bookingProcess: {
                        selectedPackageItems: {
                            $set: [],
                        },
                    },
                });

            case BookingAction.SET_SELECTED_PACKAGE_CREDIT_ITEMS:
                return update(state, {
                    bookingProcess: {
                        selectedPackageCreditItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_SELECTED_PACKAGE_CREDIT_ITEM:
                const creditItemIndex = state.bookingProcess.selectedPackageCreditItems.findIndex(item => item.personIndex === action.payload.personIndex);
                if (creditItemIndex === -1) {
                    return update(state, {
                        bookingProcess: {
                            selectedPackageCreditItems: {
                                $push: [action.payload],
                            },
                        },
                    });
                } else {
                    return update(state, {
                        bookingProcess: {
                            selectedPackageCreditItems: {
                                [creditItemIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }

            case BookingAction.REMOVE_SELECTED_PACKAGE_CREDIT_ITEM:
                const creditItemToRemoveIndex = state.bookingProcess.selectedPackageCreditItems.findIndex(item => item.personIndex === action.payload);
                if (creditItemToRemoveIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            selectedPackageCreditItems: {
                                $splice: [[creditItemToRemoveIndex, 1]],
                            },
                        },
                    });
                }
                return state;

            case BookingAction.RESET_SELECTED_PACKAGE_CREDIT_ITEMS:
                return update(state, {
                    bookingProcess: {
                        selectedPackageCreditItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_TIMESLOTS_ALLOWED:
                return update(state, {
                    timeslotsAllowed: {
                        $set: action.payload,
                    },
                });
            case BookingAction.SET_TIMESLOTS:
                return update(state, {
                    timeslots: {
                        $set: action.payload,
                    },
                });

            case BookingAction.SET_PACKAGE_CART:
                return update(state, {
                    bookingProcess: {
                        packageCart: {
                            $set: action.payload,
                        },
                    },
                });
            case BookingAction.SET_FILTER:
                action.payload.adults = +action.payload.adults;
                action.payload.children = +action.payload.children;
                return update(state, {
                    bookingProcess: {
                        filter: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_FILTER_ADULTS_COUNT:
                action.payload = +action.payload;
                return update(state, {
                    bookingProcess: {
                        filter: {
                            adults: {
                                $set: action.payload,
                            },
                        },
                    },
                });

            case BookingAction.SET_FILTER_CHILDREN_COUNT:
                action.payload = +action.payload;
                return update(state, {
                    bookingProcess: {
                        filter: {
                            children: {
                                $set: action.payload,
                            },
                        },
                    },
                });

            case BookingAction.SET_PACKAGE:
                return update(state, {
                    bookingProcess: {
                        package: {
                            $set: action.payload,
                        },
                    },
                });
            case BookingAction.RESET_RESERVATION_NOTE:
                return update(state, {
                    bookingProcess: {
                        notes: {
                            $set: [...initBookingProcess.notes],
                        },
                    },
                });

            case BookingAction.PUSH_RESERVATION_NOTE:
                const notesIndex = state.bookingProcess.notes.findIndex(note => note.id === action.payload.id);
                if (notesIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            notes: {
                                [notesIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }
                return update(state, {
                    bookingProcess: {
                        notes: {
                            $push: [action.payload],
                        },
                    },
                });

            case BookingAction.SET_UPSELL:
                const upsellFoundIndex = state.bookingProcess.upsell.findIndex(upsell => upsell.packageItem.id === action.payload.packageItem.id);
                if (upsellFoundIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            upsell: {
                                [upsellFoundIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }
                return state;

            case BookingAction.PUSH_UPSELL:
                const upsellIndex = state.bookingProcess.upsell.findIndex(upsell => upsell.packageItem.id === action.payload.packageItem.id);

                if (upsellIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            upsell: {
                                [upsellIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }

                return update(state, {
                    bookingProcess: {
                        upsell: {
                            $push: [action.payload],
                        },
                    },
                });

            case BookingAction.REMOVE_UPSELL:
                const filteredUpsells = state.bookingProcess.upsell?.filter(upsell => upsell.packageItem.id !== action.payload.packageItem.id);

                return update(state, {
                    bookingProcess: {
                        upsell: {
                            $set: filteredUpsells,
                        },
                    },
                });

            case BookingAction.SET_COMPETITION:
                const competitionFoundIndex = state.bookingProcess.competition.findIndex(
                    competition => competition.packageItem.id === action.payload.packageItem.id
                );
                if (competitionFoundIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            competition: {
                                [competitionFoundIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }
                return state;

            case BookingAction.PUSH_COMPETITION:
                const competitionIndex = state.bookingProcess.competition.findIndex(
                    competition => competition.packageItem.id === action.payload.packageItem.id
                );

                if (competitionIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            competition: {
                                [competitionIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }

                return update(state, {
                    bookingProcess: {
                        competition: {
                            $push: [action.payload],
                        },
                    },
                });

            case BookingAction.REMOVE_COMPETITION:
                const filteredCompetitions = state.bookingProcess.competition?.filter(
                    competition => competition.packageItem.id !== action.payload.packageItem.id
                );

                return update(state, {
                    bookingProcess: {
                        competition: {
                            $set: filteredCompetitions,
                        },
                    },
                });

            case BookingAction.CLEAR_COMPETITIONS:
                return update(state, {
                    bookingProcess: {
                        competition: {
                            $set: [],
                        },
                    },
                });

            case BookingAction.SET_CUSTOMER:
                return update(state, {
                    bookingProcess: {
                        customer: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.RESET_CUSTOMER:
                return update(state, {
                    bookingProcess: {
                        customer: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_COOKIES:
                return update(state, {
                    cookies: {
                        $set: action.payload,
                    },
                });

            case BookingAction.SET_PACKAGES_CART_PAYMENT:
                return update(state, {
                    bookingProcess: {
                        payment: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_PACKAGES_CART_PAYMENT_ASSERT:
                return update(state, {
                    bookingProcess: {
                        paymentAssert: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_CURRENT_NAVIGATION_STEP:
                return update(state, {
                    navigation: {
                        currentStep: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.CLEAR_BOOKING_PROCESS_STATE:
                return update(state, {
                    bookingProcess: {
                        $set: {
                            ...initBookingProcess,
                        },
                    },
                });

            case BookingAction.CLEAR_UPSELLS:
                return update(state, {
                    bookingProcess: {
                        upsell: {
                            $set: [],
                        },
                    },
                });

            case BookingAction.SET_PACKAGE_INFO:
                return update(state, {
                    bookingProcess: {
                        packageInfo: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_RENOUNCE_FREE_COMPETITIONS:
                return update(state, {
                    bookingProcess: {
                        renounceFreeCompetitions: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_AVAILABLE_COMPETITION_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availableCompetitionItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.CLEAR_AVAILABLE_COMPETITION_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availableCompetitionItems: {
                            $set: [],
                        },
                    },
                });

            case BookingAction.SET_AVAILABLE_INCLUDED_COMPETITION_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availableIncludedCompetitionItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.CLEAR_AVAILABLE_INCLUDED_COMPETITION_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availableIncludedCompetitionItems: {
                            $set: [],
                        },
                    },
                });

            default:
                return state;
        }
    };
}

export default BookingReducer;
